import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ShieldAlert, CheckCircle2, XCircle, AlertTriangle, Clock, Server, ArrowLeft, FileSearch, Layers, Zap, BookOpen } from 'lucide-react'
import API from '../../services/api'

const SEVERITY_STYLES = {
  HIGH: 'bg-red-500/10 text-red-400 border border-red-500/20',
  MEDIUM: 'bg-orange-500/10 text-orange-400 border border-orange-500/20',
  LOW: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20',
  INFO: 'bg-gray-500/10 text-gray-400 border border-gray-500/20',
}

const CATEGORY_META = {
  tool_scan: { label: 'Tool Analysis', icon: Layers, color: 'text-blue-400' },
  prompt_scan: { label: 'Prompt Analysis', icon: BookOpen, color: 'text-purple-400' },
  resource_scan: { label: 'Resource Analysis', icon: FileSearch, color: 'text-yellow-400' },
  readiness_scan: { label: 'Readiness Assessment', icon: Zap, color: 'text-green-400' },
}

function SeverityBadge({ severity }) {
  const style = SEVERITY_STYLES[severity?.toUpperCase()] || SEVERITY_STYLES.INFO
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}>
      {severity || 'Unknown'}
    </span>
  )
}


function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center px-6">
      <ShieldAlert size={40} className="text-gray-600 mb-3" />
      <p className="text-gray-500 text-base">{message}</p>
    </div>
  )
}

function ToolScanSection({ parsed }) {
  const { summary, unsafe_tools = [] } = parsed
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-gray-400 text-sm mb-1">Tools Scanned</p>
          <p className="text-white text-3xl font-bold">{summary?.tools_scanned ?? '—'}</p>
        </div>
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-green-400 text-sm mb-1">Safe Tools</p>
          <p className="text-white text-3xl font-bold">{summary?.safe_tools ?? '—'}</p>
        </div>
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-red-400 text-sm mb-1">Unsafe Tools</p>
          <p className="text-white text-3xl font-bold">{summary?.unsafe_tools ?? '—'}</p>
        </div>
      </div>

      {unsafe_tools.length === 0 ? (
        <EmptyState message="No unsafe tools detected." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#232325]">
          <table className="w-full text-white min-w-[600px]">
            <thead>
              <tr className="bg-[#232325] text-left">
                <th className="px-5 py-4 text-gray-400 text-sm font-medium">#</th>
                <th className="px-5 py-4 text-gray-400 text-sm font-medium">Tool Name</th>
                <th className="px-5 py-4 text-gray-400 text-sm font-medium">Findings</th>
                <th className="px-5 py-4 text-gray-400 text-sm font-medium">Severity</th>
                <th className="px-5 py-4 text-gray-400 text-sm font-medium">Threats Detected</th>
              </tr>
            </thead>
            <tbody>
              {unsafe_tools.map((tool, i) => (
                tool.findings?.map((finding, j) => (
                  <tr key={`${i}-${j}`} className="border-t border-[#232325] hover:bg-[#1a1a1c] transition-colors">
                    {j === 0 && (
                      <>
                        <td className="px-5 py-4 text-gray-400 text-sm" rowSpan={tool.findings.length}>{i + 1}</td>
                        <td className="px-5 py-4 text-gray-200 text-sm font-semibold" rowSpan={tool.findings.length}>
                          {tool.name || '—'}
                        </td>
                      </>
                    )}
                    <td className="px-5 py-4 text-gray-300 text-sm">{finding.description || '—'}</td>
                    <td className="px-5 py-4"><SeverityBadge severity={finding.severity} /></td>
                    <td className="px-5 py-4">
                      {finding.threats?.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {finding.threats.map((t, k) => (
                            <span key={k} className="bg-red-500/10 text-red-400 border border-red-500/20 px-2.5 py-1 rounded-full text-xs font-medium">{t}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">None</span>
                      )}
                    </td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function PromptScanSection({ parsed }) {
  const { summary, safe_prompts = [], unsafe_prompts = [] } = parsed
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-gray-400 text-sm mb-1">Prompts Scanned</p>
          <p className="text-white text-3xl font-bold">{summary?.prompts_scanned ?? '—'}</p>
        </div>
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-green-400 text-sm mb-1">Safe Prompts</p>
          <p className="text-white text-3xl font-bold">{summary?.safe_prompts ?? '—'}</p>
        </div>
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-red-400 text-sm mb-1">Unsafe Prompts</p>
          <p className="text-white text-3xl font-bold">{summary?.unsafe_prompts ?? '—'}</p>
        </div>
      </div>

      {unsafe_prompts.length > 0 && (
        <div>
          <p className="text-red-400 text-base font-semibold mb-3">Unsafe Prompts</p>
          <div className="space-y-3">
            {unsafe_prompts.map((p, i) => (
              <div key={i} className="border border-red-500/20 bg-red-500/5 rounded-xl px-5 py-4">
                <p className="text-white text-base font-semibold">{p.name || '—'}</p>
                {p.description && <p className="text-gray-400 text-sm mt-1">{p.description}</p>}
                {p.findings?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {p.findings.map((f, j) => (
                      <div key={j} className="flex items-start gap-2 text-sm text-gray-300">
                        <SeverityBadge severity={f.severity} />
                        <span>{f.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {safe_prompts.length > 0 && (
        <div>
          <p className="text-green-400 text-base font-semibold mb-3">Safe Prompts</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {safe_prompts.map((p, i) => (
              <div key={i} className="border border-[#232325] rounded-xl px-5 py-4 flex items-start gap-3">
                <CheckCircle2 size={18} className="text-green-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-white text-base font-semibold">{p.name || '—'}</p>
                  {p.description && <p className="text-gray-400 text-sm mt-1">{p.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {safe_prompts.length === 0 && unsafe_prompts.length === 0 && (
        <EmptyState message="No prompt data available." />
      )}
    </div>
  )
}

function ResourceScanSection({ parsed }) {
  const { summary, skipped_resources = [], unsafe_resources = [], log_messages = [] } = parsed

  const errors = log_messages.filter(l => l.level === 'ERROR')
  const warnings = log_messages.filter(l => l.level === 'WARNING')

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-4">
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-gray-400 text-sm mb-1">Resources Found</p>
          <p className="text-white text-3xl font-bold">{summary?.resources_found ?? '—'}</p>
        </div>
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-blue-400 text-sm mb-1">Scanned</p>
          <p className="text-white text-3xl font-bold">{summary?.scanned ?? '—'}</p>
        </div>
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-yellow-400 text-sm mb-1">Skipped</p>
          <p className="text-white text-3xl font-bold">{summary?.skipped ?? '—'}</p>
        </div>
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-red-400 text-sm mb-1">Failed</p>
          <p className="text-white text-3xl font-bold">{summary?.failed ?? '—'}</p>
        </div>
      </div>

      {unsafe_resources.length > 0 && (
        <div>
          <p className="text-red-400 text-base font-semibold mb-3">Unsafe Resources</p>
          {unsafe_resources.map((r, i) => (
            <div key={i} className="border border-red-500/20 bg-red-500/5 rounded-xl px-5 py-4 mb-3">
              <p className="text-white text-base font-semibold">{r.name || r.uri || '—'}</p>
              {r.uri && <p className="text-gray-400 text-sm font-mono mt-1">{r.uri}</p>}
            </div>
          ))}
        </div>
      )}

      {skipped_resources.length > 0 && (
        <div>
          <p className="text-yellow-400 text-base font-semibold mb-3">Skipped Resources</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {skipped_resources.map((r, i) => (
              <div key={i} className="border border-[#232325] rounded-xl px-5 py-4">
                <p className="text-white text-base font-semibold">{r.name || '—'}</p>
                {r.uri && <p className="text-gray-500 text-sm font-mono mt-1 truncate">{r.uri}</p>}
                {r.mime_type && <p className="text-gray-500 text-sm mt-1">{r.mime_type}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div>
          <p className="text-red-400 text-base font-semibold mb-3">Errors</p>
          <div className="space-y-2">
            {errors.map((log, i) => (
              <div key={i} className="border border-red-500/20 bg-red-500/5 rounded-lg px-5 py-3 flex items-start gap-3">
                <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">{log.message}</p>
                  {log.timestamp && <p className="text-gray-500 text-xs mt-1 font-mono">{log.timestamp}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {warnings.length > 0 && (
        <div>
          <p className="text-yellow-400 text-base font-semibold mb-3">Warnings</p>
          <div className="space-y-2">
            {warnings.map((log, i) => (
              <div key={i} className="border border-yellow-500/20 bg-yellow-500/5 rounded-lg px-5 py-3 flex items-start gap-3">
                <AlertTriangle size={16} className="text-yellow-400 mt-0.5 shrink-0" />
                <p className="text-gray-300 text-sm">{log.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {skipped_resources.length === 0 && unsafe_resources.length === 0 && errors.length === 0 && (
        <EmptyState message="No resource data to display." />
      )}
    </div>
  )
}

function ReadinessScanSection({ parsed }) {
  const { summary, unsafe_tools = [] } = parsed
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-gray-400 text-sm mb-1">Tools Scanned</p>
          <p className="text-white text-3xl font-bold">{summary?.tools_scanned ?? '—'}</p>
        </div>
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-green-400 text-sm mb-1">Ready</p>
          <p className="text-white text-3xl font-bold">{summary?.safe_tools ?? '—'}</p>
        </div>
        <div className="border border-[#232325] rounded-xl px-5 py-4 text-center">
          <p className="text-red-400 text-sm mb-1">Issues Found</p>
          <p className="text-white text-3xl font-bold">{summary?.unsafe_tools ?? '—'}</p>
        </div>
      </div>

      {unsafe_tools.length === 0 ? (
        <EmptyState message="All tools passed readiness checks." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#232325]">
          <table className="w-full text-white min-w-[500px]">
            <thead>
              <tr className="bg-[#232325] text-left">
                <th className="px-5 py-4 text-gray-400 text-sm font-medium">#</th>
                <th className="px-5 py-4 text-gray-400 text-sm font-medium">Tool Name</th>
                <th className="px-5 py-4 text-gray-400 text-sm font-medium">Issue</th>
                <th className="px-5 py-4 text-gray-400 text-sm font-medium">Severity</th>
              </tr>
            </thead>
            <tbody>
              {unsafe_tools.map((tool, i) => (
                tool.findings?.map((finding, j) => (
                  <tr key={`${i}-${j}`} className="border-t border-[#232325] hover:bg-[#1a1a1c] transition-colors">
                    {j === 0 && (
                      <>
                        <td className="px-5 py-4 text-gray-400 text-sm" rowSpan={tool.findings.length}>{i + 1}</td>
                        <td className="px-5 py-4 text-gray-200 text-sm font-semibold" rowSpan={tool.findings.length}>
                          {tool.name || '—'}
                        </td>
                      </>
                    )}
                    <td className="px-5 py-4 text-gray-300 text-sm">{finding.description || '—'}</td>
                    <td className="px-5 py-4"><SeverityBadge severity={finding.severity} /></td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ScanSection({ scan }) {
  const meta = CATEGORY_META[scan.parsed?.scan_category] || { label: scan.parsed?.scan_category || 'Unknown Scan', icon: ShieldAlert, color: 'text-gray-400' }
  const Icon = meta.icon

  const renderContent = () => {
    switch (scan.parsed?.scan_category) {
      case 'tool_scan': return <ToolScanSection parsed={scan.parsed} />
      case 'prompt_scan': return <PromptScanSection parsed={scan.parsed} />
      case 'resource_scan': return <ResourceScanSection parsed={scan.parsed} />
      case 'readiness_scan': return <ReadinessScanSection parsed={scan.parsed} />
      default: return <EmptyState message="Unknown scan category. No data to display." />
    }
  }

  return (
    <div className="bg-[#101012] border border-[#232325] rounded-2xl overflow-hidden">
      <div className="px-6 py-5 border-b border-[#232325] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon size={20} className={meta.color} />
          <p className={`font-bold text-lg ${meta.color}`}>{meta.label}</p>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          {scan.parsed?.server_url && (
            <span className="flex items-center gap-1.5">
              <Server size={14} />
              <span className="font-mono">{scan.parsed.server_url}</span>
            </span>
          )}
          {scan.timestamp && (
            <span className="flex items-center gap-1.5">
              <Clock size={14} />
              {new Date(scan.timestamp).toLocaleString()}
            </span>
          )}
        </div>
      </div>
      <div className="p-6">
        {renderContent()}
      </div>
    </div>
  )
}


export default function McpScanDetail() {
  const { assetName, filename } = useParams()
  const navigate = useNavigate()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await API.get('/mcp/scan-file/', {
          params: {
            asset_name: decodeURIComponent(assetName),
            filename: decodeURIComponent(filename),
          },
        })
        setReport(res.data)
      } catch {
        setError('Failed to load scan report.')
      } finally {
        setLoading(false)
      }
    }
    fetchReport()
  }, [assetName, filename])


  const serverUrls = [...new Set(
    report?.scans?.map(s => (s.data?.parsed || s.parsed)?.server_url).filter(Boolean) || []
  )]

  const scans = report?.scans?.map(s => s.data || s) || []

  return (
    <div className="p-6 sm:p-8 text-white min-h-screen">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/mcp-scan')}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-base transition-colors"
        >
          <ArrowLeft size={18} /> Back
        </button>
      </div>

      <div className="mb-8">
        <p className="text-white font-bold text-3xl">MCP Scan Report</p>
        <p className="text-gray-400 text-base mt-1">Detailed results of the selected MCP scan session</p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-24 text-gray-500 text-sm">Loading report…</div>
      )}

      {error && (
        <div className="border border-red-500/20 bg-red-500/5 rounded-xl px-5 py-4 text-red-400 text-sm">{error}</div>
      )}

      {!loading && !error && !report && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <ShieldAlert size={40} className="text-gray-600 mb-3" />
          <p className="text-gray-400 text-sm">No report data available. The fetch endpoint is not yet connected.</p>
        </div>
      )}

      {report && (
        <div className="space-y-6">
          {serverUrls.length > 0 && (
            <div className="bg-[#101012] border border-[#232325] rounded-xl px-5 py-4 flex items-center gap-2">
              <Server size={16} className="text-gray-400 shrink-0" />
              <span className="text-gray-400 text-sm">Target Server:</span>
              <span className="text-gray-200 font-mono text-sm">{serverUrls.join(', ')}</span>
            </div>
          )}

          {/* Per-scan sections */}
          {scans.length === 0 ? (
            <EmptyState message="No scan data found in this report." />
          ) : (
            scans.map((scan, i) => <ScanSection key={i} scan={scan} />)
          )}
        </div>
      )}
    </div>
  )
}
